import { ShoppingCart, Menu, X, User, ShieldCheck, LogOut } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const Navbar = ({ cartCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const linkCls = (path) =>
    `font-medium transition-colors ${isActive(path) ? 'text-primary' : 'text-secondary hover:text-primary'}`;

  return (
    <nav className="bg-white border-b border-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="font-heading font-bold text-2xl tracking-tighter text-primary" data-testid="logo-link">
            SOLARINNOV
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={linkCls('/')} data-testid="nav-home-link">Accueil</Link>
            <Link to="/produits" className={linkCls('/produits')} data-testid="nav-products-link">Produits</Link>
            <Link to="/services" className={linkCls('/services')} data-testid="nav-services-link">Services</Link>
            <Link to="/devis" className={linkCls('/devis')} data-testid="nav-quotes-link">Devis</Link>
            <Link to="/contact" className={linkCls('/contact')} data-testid="nav-contact-link">Contact</Link>

            {/* Auth menu */}
            {!user ? (
              <Link
                to="/connexion"
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-full font-medium transition-all"
                data-testid="nav-login-link"
              >
                <User className="w-4 h-4" />
                Connexion
              </Link>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen((o) => !o)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:bg-muted font-medium text-secondary"
                  data-testid="nav-user-menu-btn"
                >
                  {isAdmin ? <ShieldCheck className="w-4 h-4 text-accent" /> : <User className="w-4 h-4" />}
                  <span className="text-sm">{user.name || user.email}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-border rounded-xl shadow-xl overflow-hidden" data-testid="nav-user-menu">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-medium text-secondary truncate">{user.email}</p>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">
                        {isAdmin ? 'Administrateur' : 'Client'}
                      </p>
                    </div>
                    {isAdmin ? (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 hover:bg-muted text-secondary text-sm"
                        data-testid="nav-admin-link"
                      >
                        <ShieldCheck className="w-4 h-4 text-accent" />
                        Tableau de bord Admin
                      </Link>
                    ) : (
                      <Link
                        to="/mon-compte"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 hover:bg-muted text-secondary text-sm"
                        data-testid="nav-mon-compte-link"
                      >
                        <User className="w-4 h-4" />
                        Mon compte
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 hover:bg-red-50 text-red-600 text-sm border-t border-border"
                      data-testid="nav-logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            )}

            <Link
              to="/panier"
              className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted transition-colors"
              data-testid="nav-cart-link"
            >
              <ShoppingCart className="w-5 h-5 text-secondary" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden" data-testid="mobile-menu-button">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-border" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to="/" className="font-medium text-secondary hover:text-primary" onClick={() => setIsOpen(false)}>Accueil</Link>
              <Link to="/produits" className="font-medium text-secondary hover:text-primary" onClick={() => setIsOpen(false)}>Produits</Link>
              <Link to="/services" className="font-medium text-secondary hover:text-primary" onClick={() => setIsOpen(false)}>Services</Link>
              <Link to="/devis" className="font-medium text-secondary hover:text-primary" onClick={() => setIsOpen(false)}>Devis</Link>
              <Link to="/contact" className="font-medium text-secondary hover:text-primary" onClick={() => setIsOpen(false)}>Contact</Link>
              {!user ? (
                <Link to="/connexion" className="font-medium text-primary" onClick={() => setIsOpen(false)}>Connexion</Link>
              ) : (
                <>
                  <Link to={isAdmin ? '/admin' : '/mon-compte'} className="font-medium text-primary" onClick={() => setIsOpen(false)}>
                    {isAdmin ? 'Admin' : 'Mon compte'}
                  </Link>
                  <button onClick={() => { handleLogout(); setIsOpen(false); }} className="text-left font-medium text-red-600">Déconnexion</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
